import React, { useCallback, useContext, useEffect, useState } from 'react'
import { CHANNEL_PATH } from './constants'
import VizSettings from './VizSettings'
import WheelGraph from './WheelGraph'
import GraphContext, { GraphContextType } from './GraphContext'
import UserContext, { UserContextType } from './UserContext'
import PrintContext, { getManifest, PrintContextType } from './PrintContext'
import WheelActions from './WheelActions'
import PlayControls from './PlayControls'
import realtimeService from './RealtimeService'

const GRAPH_SIZE_PERCENTAGE = 0.95
const CONTROLS_WIDTH = 280
const WHEEL_ACTIONS_HEIGHT = 22
const BREAKPOINT1 = 800

type Props = { onLoadFile: (file: File) => void }

const WheelWell = ({ onLoadFile }: Props) => {
  const {
    userContext: {
      isShowControls: isShowSettings,
      isAdvanced,
      isAutoResize,
      version,
      isBroadcasting,
      isReceiving,
      channelId,
    },
  } = useContext(UserContext) as UserContextType
  // TODO: prevent both isBroadcasting and isReceiving at same time
  const isStackVertical = window.matchMedia(`( max-width: ${BREAKPOINT1}px )`).matches
  const {
    graphContext: { graphControls: controls, frameCount, frameNumber, graphConfig: config },
    setFrame,
    updateControlValue,
    updateControlParam,
  } = useContext(GraphContext) as GraphContextType
  const { setIsAnimate, setAnimationType, setAnimationMillis, setFrameNumber, printContext } =
    useContext(PrintContext) as PrintContextType
  const {
    isAnimate,
    animationType,
    imageSize: { width: imageWidth },
    animationMillis,
  } = printContext
  const [isRealtimeInited, setIsRealtimeInited] = useState(false)
  const [realtimeTimestamp, setRealtimeTimestamp] = useState(0)
  const calcSize = useCallback(() => {
    if (!isAutoResize) {
      return imageWidth
    }
    const maxWidth = window.innerWidth - (isShowSettings && !isStackVertical ? CONTROLS_WIDTH : 0)
    const minDim = Math.min(maxWidth, window.innerHeight - WHEEL_ACTIONS_HEIGHT)
    const newSize = Math.floor(GRAPH_SIZE_PERCENTAGE * minDim)
    return newSize
  }, [isAutoResize, imageWidth, isShowSettings, isStackVertical])
  const [workingImageWidth, setWorkingImageWidth] = useState<number>(calcSize())

  useEffect(() => {
    setWorkingImageWidth(calcSize())
  }, [calcSize])
  useEffect(() => {
    let timeout: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        const newSize = calcSize()
        setWorkingImageWidth(newSize)
      }, 200)
    }
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [calcSize])
  useEffect(() => {
    if (isAnimate) {
      startAnimation(animationMillis)
    } else {
      stopAnimation()
    }
  }, [isAnimate])

  const playCallback = React.useCallback(
    (snapshot: any) => {
      const controlData = snapshot.val() || {}
      const { timestamp, isPlay, isActive } = controlData
      console.log(controlData)
      setRealtimeTimestamp((prevState) => timestamp)
      if (timestamp > realtimeTimestamp) {
        console.log(`isPlay: ${isPlay}, ${timestamp} > ${realtimeTimestamp}`)
        setIsAnimate(isPlay)
      }
    },
    [realtimeTimestamp, setIsAnimate]
  )
  useEffect(() => {
    if (isReceiving && channelId && !isRealtimeInited) {
      const controlPath = `${CHANNEL_PATH}/${channelId}/control`
      realtimeService.endpoint(controlPath).on('value', playCallback)
      setIsRealtimeInited(true)
    }
  }, [playCallback, channelId, isReceiving, isRealtimeInited])
  const broadcastCallback = React.useCallback(() => {
    const multipolarJson = getManifest(printContext, controls, config, version)
    const session = {
      control: {
        // initialPath,
        timestamp: Date.now(),
        isPlay: false,
        isActive: true,
      },
      graph: multipolarJson,
    }
    const path = `${CHANNEL_PATH}/${channelId}`
    realtimeService.set(path, session)
  }, [printContext, controls, config, version, channelId])
  useEffect(() => {
    if (isBroadcasting && !isAnimate) {
      broadcastCallback()
    }
  }, [isBroadcasting, broadcastCallback, isAnimate])

  const [animationLoopId, setAnimationLoopId] = useState<NodeJS.Timer>()

  const startAnimation = (animationMillis: number, isForce?: boolean) => {
    // const animationMillis = frameRate / 60 / 100 // TODO: switch to frameRate
    if (!animationLoopId || isForce) {
      clearTimeout(animationLoopId)
      let nextFrameNumber = frameNumber
      let isAscending = true

      const id = setInterval(() => {
        if (animationType === 1) {
          if (isAscending && nextFrameNumber === frameCount) {
            isAscending = false
            nextFrameNumber--
          } else if (!isAscending && nextFrameNumber === 1) {
            isAscending = true
            nextFrameNumber++
          } else {
            nextFrameNumber = nextFrameNumber + (isAscending ? 1 : -1)
          }
        } else {
          nextFrameNumber = (nextFrameNumber % frameCount) + 1
        }
        setFrame(nextFrameNumber)
        setFrameNumber(nextFrameNumber)
      }, animationMillis)
      // console.log(`(re)starting animation at ${animationMillis}`)
      setAnimationLoopId(id)
    }
  }
  const stopAnimation = () => {
    clearTimeout(animationLoopId)
    setAnimationLoopId(undefined)
  }

  const toggleIsAnimate = () => {
    const newIsAnimate = !isAnimate
    setIsAnimate(newIsAnimate)
    if (isBroadcasting) {
      const path = `${CHANNEL_PATH}/${channelId}/control`
      realtimeService.set(path, { timestamp: Date.now(), isPlay: newIsAnimate, isActive: true })
    }
  }
  const toggleIsReverse = () => {
    setAnimationType(animationType ? 0 : 1)
  }
  const updateAnimationMillis = (newAnimationMillis: number) => {
    if (newAnimationMillis !== animationMillis && isAnimate) {
      startAnimation(newAnimationMillis, true)
    }
    setAnimationMillis(newAnimationMillis)
  }

  return (
    <div className="wheelWell">
      {isShowSettings && (
        <VizSettings
          controls={controls}
          updateControl={updateControlValue}
          updateConfig={updateControlParam}
        />
      )}
      <div className="mainPanel">
        {isShowSettings && (
          <div className="controlBar">
            <div className="viewOptions">
              {frameCount > 1 && (
                <PlayControls
                  toggleIsReverse={toggleIsReverse}
                  toggleIsAnimate={toggleIsAnimate}
                  updateAnimationMillis={updateAnimationMillis}
                />
              )}
            </div>
          </div>
        )}
        <WheelGraph
          width={workingImageWidth}
          toggleIsAnimate={toggleIsAnimate}
          isEditing={isShowSettings}
        />
        <WheelActions isShowAdvanced={isAdvanced} onLoadFile={onLoadFile} />
      </div>
    </div>
  )
}

export default WheelWell
